import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

// Packages
import { Button, ColorPicker, Form, Input, InputNumber, Select, Skeleton, Tag, Tooltip } from 'antd';
import { CameraFilled, CloseOutlined, CrownFilled, InfoCircleOutlined, LeftOutlined, LockFilled, PictureFilled, PlusOutlined, RiseOutlined, StarFilled, TrophyFilled, UserOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import ImageUpload from '../../utils/ImageUpload';
import ImageRender from '../../utils/ImageRender';
import WorldPacksCard from './WorldPacksCard';

// Actions
import { clear_all_worlds_data, get_all_worlds, get_world_info, world_update } from '../../../redux/actions/worldsActions';
import { get_all_medals } from '../../../redux/actions/medalsActions';
import { get_all_recipes } from '../../../redux/actions/recipesActions';
import { get_all_packages } from '../../../redux/actions/packagesActions';
import { form_image_upload } from '../../../redux/actions/utilsActions';

// Utils
import isEmpty from '../../../utils/isEmpty';
import { format_hex_color_with_alpha, transform_color_format } from '../../../utils/colors_handlers';
import { world_tier_medals, upsert_world_medal, object_ref_id, MEDAL_TIER_SILVER, MEDAL_TIER_GOLD } from '../../../utils/world_medals';
import { premium_world_ids, free_recipes_count_by_world } from '../../../utils/star_goals';

// Brand palette for the theme-color picker ("Colores de marca" in the design).
const BRAND_PALETTE = ['#A2D7DC', '#F18B71', '#E0DD6B', '#EF85A7', '#FCC354', '#80B4B9', '#B6ECF1', '#FFDBD2', '#3F3F3F'];

// Tier visual tokens from the "Editar Mundo (pantalla)" design.
const TIER_STYLES = {
	[MEDAL_TIER_SILVER]: {
		border: '#DDE1E6',
		bg: 'linear-gradient(180deg, #FBFCFD, #EEF2F6)',
		iconGrad: 'linear-gradient(150deg, #D3D8DE, #9AA3AD)',
		subColor: '#8A929B',
		noteColor: '#7A828B'
	},
	[MEDAL_TIER_GOLD]: {
		border: '#EAD5A0',
		bg: 'linear-gradient(180deg, #FFFBF0, #FBF1D6)',
		iconGrad: 'linear-gradient(150deg, #FCD97A, #D99A1E)',
		subColor: '#A78334',
		noteColor: '#A07A28'
	}
};

const cardStyle = {
	background: '#fff',
	border: '1px solid #efedec',
	borderRadius: 16,
	boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
	padding: '24px 26px',
	marginBottom: 20
};

const sectionIconStyle = {
	width: 30,
	height: 30,
	borderRadius: 9,
	background: '#FFEDE9',
	color: '#B75D46',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: 'none'
};

const WorldDetails = (props) => {
	const { messageApi } = props;

	const { world_id } = useParams();

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const navigate = useNavigate();

	const { world_info_loading, world_info, all_worlds } = useSelector(state => state.worlds);

	const { all_medals } = useSelector(state => state.medals);

	const { all_recipes } = useSelector(state => state.recipes);

	const { all_packages } = useSelector(state => state.packages);

	const [patternFile, setPatternFile] = useState([]);
	const [imageFile, setImageFile] = useState([]);
	const [medals, setMedals] = useState([]);
	const [medalFiles, setMedalFiles] = useState({});

	const [form] = Form.useForm();

	const previewName = Form.useWatch('name', form);
	const previewNationality = Form.useWatch('nationality', form);
	const previewColor = Form.useWatch('color', form);

	const world = world_info?.world ?? world_info;

	// The map order is read-only here: it changes ONLY through the atomic
	// "reorder worlds" drag & drop on the worlds list.
	const savedOrder = world?.order ?? 0;

	const starLocked = savedOrder === 0;

	// Star-goal context: what a player can have accumulated BEFORE this world
	// (best run = 5 stars per recipe) and the previous world's numbers, so the
	// admin sets an absolute threshold that is reachable and strictly growing.
	const orderNum = savedOrder;

	const freeCounts = free_recipes_count_by_world(
		all_recipes?.recipes, all_packages?.packages
	);

	const premiumIds = premium_world_ids(all_packages?.packages);

	const isPremiumWorld = premiumIds.has(world?._id?.$oid ?? world_id);

	const previousWorlds = (all_worlds?.worlds ?? []).filter((w) => (
		object_ref_id(w._id) !== world_id &&
		!premiumIds.has(object_ref_id(w._id)) &&
		(w.order ?? 0) < orderNum
	));

	const previousWorld = previousWorlds.find((w) => (w.order ?? 0) === orderNum - 1);

	const maxStars = previousWorlds.reduce((sum, w) => (
		sum + (freeCounts[object_ref_id(w._id)] ?? 0) * 5
	), 0);

	const previousGoal = previousWorld?.star_goal ?? 0;

	const minStars = previousWorlds.length ? previousGoal + 1 : 0;

	const previousRecipes = previousWorld
		? freeCounts[object_ref_id(previousWorld._id)] ?? 0
		: 0;

	const previewHex = typeof previewColor === 'string'
		? previewColor
		: previewColor?.toHex?.() ?? '';

	useEffect(() => {
		dispatch(get_world_info(world_id));
		dispatch(get_all_medals({ skip: 0, limit: 0 }));
		dispatch(get_all_worlds({ skip: 0, limit: 0 }));
		dispatch(get_all_recipes({ skip: 0, limit: 0 }));
		dispatch(get_all_packages({ skip: 0, limit: 0 }));

		return () => {
			dispatch(clear_all_worlds_data());
		}
	}, [dispatch, world_id]);

	useEffect(() => {
		if (!isEmpty(world) && world?.name !== undefined) {
			form.setFieldsValue({
				name: world?.name,
				description: world?.description,
				nationality: world?.nationality,
				color: transform_color_format(world?.color),
				star_goal: world?.star_goal ?? 0,
			});
		}
	}, [world_info]);

	useEffect(() => {
		if (!all_medals?.medals?.length) return;

		const found = world_tier_medals(all_medals.medals, world?._id?.$oid ?? world_id);

		// Merge server medals into the cards without clobbering in-progress edits.
		setMedals((prev) => {
			const next = [...prev];

			[[MEDAL_TIER_SILVER, found.silver], [MEDAL_TIER_GOLD, found.gold]]
				.forEach(([tier, medal]) => {
					if (!medal) return;

					const card = {
						key: tier === MEDAL_TIER_SILVER ? 'silver' : 'gold',
						tier,
						name: medal.name ?? '',
						description: medal.description ?? '',
						existing: medal
					};

					const i = next.findIndex((m) => m.tier === tier);
					if (i >= 0) {
						next[i] = {
							...card,
							name: next[i].name || card.name,
							description: next[i].description || card.description
						};
					}
					else {
						next.push(card);
					}
				});

			return next;
		});
	}, [all_medals]);

	const usedTiers = medals.map((m) => m.tier);

	const setMedalField = (key, field, value) => {
		setMedals(medals.map((m) => m.key === key ? { ...m, [field]: value } : m));
	}

	const addMedal = () => {
		const tier = [MEDAL_TIER_SILVER, MEDAL_TIER_GOLD].find((x) => !usedTiers.includes(x));
		if (tier === undefined) return;
		setMedals([...medals, { key: `new-${tier}`, tier, name: '', description: '', existing: null }]);
	}

	const removeMedal = (key) => {
		setMedals(medals.filter((m) => m.key !== key));
	}

	const refetchMedals = () => {
		dispatch(get_all_medals({ skip: 0, limit: 0 }));
	}

	const saveMedal = (m) => {
		if (!m.name.trim()) {
			messageApi.error(t('form.rules.required'), 4);
			return;
		}

		upsert_world_medal(dispatch, messageApi, {
			existing: m.existing,
			worldId: world?._id?.$oid ?? world_id,
			tier: m.tier,
			name: m.name,
			description: m.description
		}, refetchMedals);
	}

	const saveWorldMedals = () => {
		medals.forEach((m) => {
			if (!m.name.trim()) return;
			saveMedal(m);
		});
	}

	const onSave = () => {
		form
			.validateFields()
			.then((values) => {
				const hexString = typeof values.color === 'string' ? values.color : values.color?.toHex();
				values.color = format_hex_color_with_alpha(hexString);

				// order 0 is the entry world: always unlocked, star goal pinned to 0
				if (starLocked) values.star_goal = 0;

				// premium worlds are out of the star chain: never touch their goal
				if (isPremiumWorld) delete values.star_goal;

				dispatch(world_update(messageApi, world_id, values, () => {
					dispatch(get_all_medals({ skip: 0, limit: 0 }));
				}));

				saveWorldMedals();
			})
			.catch((info) => {
				console.error(info);
			});
	}

	const sectionHeader = (icon, title, hint) => (
		<div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 18 }}>
			<div style={sectionIconStyle}>{icon}</div>
			<div>
				<div style={{ fontSize: 15, fontWeight: 700, color: '#3F3F3F' }}>{title}</div>
				<div style={{ fontSize: 12.5, color: '#A0A0A0' }}>{hint}</div>
			</div>
		</div>
	);

	const previewCard = (
		<div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: 22, padding: '20px 24px', flexWrap: 'wrap' }}>
			<div style={{ width: 112, height: 112, flex: 'none', borderRadius: 16, background: 'radial-gradient(120% 120% at 50% 25%, #F4FEFF, #E6F4F5)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
				<ImageRender
					url={`${process.env.REACT_APP_SERVER_URL}/api/game/worlds/${world_id}/image`}
					size={{ width: 96, height: 96 }}
				/>
			</div>
			<div style={{ flex: 1, minWidth: 180 }}>
				<div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#A0A0A0' }}>
					{t('worlds.details.preview')}
				</div>
				<div style={{ fontSize: 24, fontWeight: 700, color: '#3F3F3F', marginTop: 2 }}>{previewName}</div>
				<div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8, flexWrap: 'wrap' }}>
					<span style={{ fontSize: 13, color: '#8E8E8E' }}>
						{t('worlds.details.kitchenOf', { nationality: previewNationality ?? '' })}
					</span>
					<span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#8E8E8E' }}>
						<span style={{ width: 16, height: 16, borderRadius: 4, boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.1)', background: `#${previewHex}` }} />
						<span style={{ fontFamily: 'monospace', textTransform: 'uppercase' }}>#{previewHex}</span>
					</span>
				</div>
			</div>
			<div style={{ display: 'flex', gap: 16, flex: 'none' }}>
				{medals.filter((m) => m.existing).map((m) => (
					<div key={m.key} style={{ textAlign: 'center' }}>
						<div style={{ width: 56, height: 56, margin: '0 auto', overflow: 'hidden' }}>
							<ImageRender
								url={`${process.env.REACT_APP_SERVER_URL}/api/game/medals/${object_ref_id(m.existing)}/asset`}
								size={{ width: 56, height: 56 }}
							/>
						</div>
						<div style={{ fontSize: 11, color: '#8E8E8E', marginTop: 4 }}>
							{m.tier === MEDAL_TIER_GOLD ? t('form.label.worldMedalGold') : t('form.label.worldMedalSilver')}
						</div>
					</div>
				))}
			</div>
		</div>
	);

	const starGoalPanel = (
		<div style={{ border: '1px solid #F6DFAE', background: 'linear-gradient(180deg, #FFF9EE 0%, #FFF3DF 100%)', borderRadius: 14, padding: '16px 18px' }}>
			<div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 10 }}>
				<div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(150deg, #FCC354, #E89A00)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', boxShadow: '0 3px 8px rgba(232,154,0,0.35)' }}>
					<StarFilled style={{ fontSize: 17 }} />
				</div>
				<div style={{ flex: 1 }}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
						<span style={{ fontSize: 14, fontWeight: 700, color: '#3F3F3F' }}>{t('form.label.star_goal')}</span>
						<span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 700, color: '#8A5A00', background: '#FBE3AE', padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase' }}>
							<LockFilled style={{ fontSize: 10 }} />
							{t('worlds.details.starGoalTag')}
						</span>
					</div>
					<div style={{ fontSize: 12, color: '#9A7433', marginTop: 2, lineHeight: 1.45 }}>
						{t('worlds.details.starGoalSubtitle')}
					</div>
				</div>
				<Tooltip title={t('worlds.details.starGoalHelp')} overlayStyle={{ maxWidth: 300 }}>
					<InfoCircleOutlined style={{ color: '#C9A24E', fontSize: 16, cursor: 'help' }} />
				</Tooltip>
			</div>
			<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
				<Form.Item name='star_goal' noStyle>
					<InputNumber
						min={starLocked ? 0 : minStars}
						max={!starLocked && maxStars > 0 ? maxStars : undefined}
						disabled={starLocked}
						placeholder={String(minStars)}
						prefix={<StarFilled style={{ color: '#E89A00' }} />}
						style={{ width: 150, borderColor: '#EBC988' }}
					/>
				</Form.Item>
				<span style={{ fontSize: 12, color: '#9A7433' }}>{t('worlds.details.starGoalSuffix')}</span>
			</div>
			{!starLocked && maxStars > 0 && (
				<div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, padding: '10px 12px', background: '#FFFDF6', border: '1px solid #EBD9AE', borderRadius: 9 }}>
					<span style={{ fontSize: 12, color: '#8A6A22', lineHeight: 1.5 }}>
						{t('worlds.details.starGoalAccumulated', {
							max: maxStars,
							worlds: previousWorlds.length
						})}
					</span>
					{previousWorld && (
						<span style={{ fontSize: 12, color: '#8A6A22', lineHeight: 1.5 }}>
							{t('worlds.details.starGoalPrevWorld', {
								name: previousWorld.name,
								recipes: previousRecipes,
								goal: previousGoal
							})}
						</span>
					)}
					<span style={{ fontSize: 12, fontWeight: 600, color: '#5F4718', lineHeight: 1.5 }}>
						{t('worlds.details.starGoalRange', { min: minStars, max: maxStars })}
					</span>
				</div>
			)}
			<div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 13, paddingTop: 12, borderTop: '1px dashed #EAD5A6' }}>
				<span style={{ fontSize: 11, fontWeight: 600, color: '#9A7433', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
					{t('worlds.details.accumulatedExample')}
				</span>
				{['Italiano · 0', 'Americano · 13', 'Repostería · 23'].map((chip, i) => (
					<React.Fragment key={chip}>
						{i > 0 && <RiseOutlined style={{ color: '#C9A24E', fontSize: 12 }} />}
						<span style={{ fontSize: 12, fontWeight: 600, color: '#5F4718', background: '#fff', border: '1px solid #EBD9AE', padding: '3px 9px', borderRadius: 16 }}>
							{chip}
						</span>
					</React.Fragment>
				))}
			</div>
			{starLocked && (
				<div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, padding: '10px 12px', background: '#FBF3DD', border: '1px solid #EBD9AE', borderRadius: 9 }}>
					<LockFilled style={{ color: '#B08A2E', fontSize: 14, marginTop: 1 }} />
					<span style={{ fontSize: 12, color: '#8A6A22', lineHeight: 1.5 }}>
						{t('worlds.details.starGoalZeroNote')}
					</span>
				</div>
			)}
		</div>
	);

	const medalCard = (m) => {
		const style = TIER_STYLES[m.tier];
		const isGold = m.tier === MEDAL_TIER_GOLD;
		const existingId = object_ref_id(m.existing);

		// Show the stored asset only when one exists and no fresh upload is
		// already previewing in the uploader (avoids the broken-image box and
		// the duplicate thumbnail right after uploading).
		const hasStoredAsset = Boolean(m.existing?.asset);
		const hasFreshUpload = (medalFiles[m.key] ?? []).length > 0;
		const showCurrentImage = hasStoredAsset && !hasFreshUpload;
		const otherTier = isGold ? MEDAL_TIER_SILVER : MEDAL_TIER_GOLD;
		const otherTaken = usedTiers.includes(otherTier);

		return (
			<div key={m.key} style={{ border: `1px solid ${style.border}`, background: style.bg, borderRadius: 14, padding: 18 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16 }}>
					<div style={{ width: 36, height: 36, borderRadius: 10, background: style.iconGrad, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', boxShadow: '0 3px 7px rgba(120,120,120,0.3)' }}>
						{isGold ? <CrownFilled style={{ fontSize: 18 }} /> : <TrophyFilled style={{ fontSize: 18 }} />}
					</div>
					<div style={{ flex: 1 }}>
						<div style={{ fontSize: 14, fontWeight: 700, color: '#3F3F3F' }}>
							{isGold ? t('form.label.worldMedalGold') : t('form.label.worldMedalSilver')}
						</div>
						<div style={{ fontSize: 11, color: style.subColor }}>
							{isGold ? t('worlds.details.tierGoldSub') : t('worlds.details.tierSilverSub')}
						</div>
					</div>
					{!m.existing && (
						<Button
							type='text'
							size='small'
							title={t('worlds.details.removeMedal')}
							icon={<CloseOutlined />}
							onClick={() => removeMedal(m.key)}
						/>
					)}
				</div>

				<div style={{ marginBottom: 13 }}>
					<label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#3F3F3F', marginBottom: 6 }}>
						{t('worlds.details.tierType')} <span style={{ color: '#ff4d4f' }}>*</span>
					</label>
					<Select
						value={m.tier}
						style={{ width: '100%' }}
						onChange={(value) => setMedalField(m.key, 'tier', value)}
						options={[
							{ value: MEDAL_TIER_SILVER, label: t('worlds.details.tierSilverOption'), disabled: otherTaken && otherTier === MEDAL_TIER_SILVER },
							{ value: MEDAL_TIER_GOLD, label: t('worlds.details.tierGoldOption'), disabled: otherTaken && otherTier === MEDAL_TIER_GOLD }
						]}
					/>
				</div>

				<div style={{ marginBottom: 13 }}>
					<label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#3F3F3F', marginBottom: 6 }}>
						{t('worlds.details.medalNameLabel')} <span style={{ color: '#ff4d4f' }}>*</span>
					</label>
					<Input
						placeholder={t('worlds.details.medalNamePlaceholder')}
						value={m.name}
						onChange={(e) => setMedalField(m.key, 'name', e.target.value)}
					/>
				</div>

				<div style={{ marginBottom: 13 }}>
					<label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#3F3F3F', marginBottom: 6 }}>
						{t('form.label.description')}
					</label>
					<Input.TextArea
						autoSize={{ minRows: 2, maxRows: 4 }}
						value={m.description}
						onChange={(e) => setMedalField(m.key, 'description', e.target.value)}
					/>
				</div>

				<div>
					<label style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: '#3F3F3F', marginBottom: 6 }}>
						{t('worlds.details.medalImageLabel')}
					</label>
					{existingId
						?	<div style={{ display: 'flex', alignItems: 'center', gap: 12, borderRadius: 11, border: `1px solid ${style.border}`, background: '#fff', padding: '10px 14px' }}>
								{showCurrentImage && (
									<div style={{ flex: 'none', textAlign: 'center' }}>
										<div style={{ width: 72, height: 72, overflow: 'hidden' }}>
											<ImageRender
												url={`${process.env.REACT_APP_SERVER_URL}/api/game/medals/${existingId}/asset`}
												size={{ width: 72, height: 72 }}
											/>
										</div>
										<div style={{ fontSize: 11, color: style.subColor, marginTop: 2 }}>
											{t('worlds.details.currentImage')}
										</div>
									</div>
								)}
								<div style={{ flex: 1, minWidth: 0 }}>
									<ImageUpload
										action={process.env.REACT_APP_SERVER_URL + `/api/game/medals/${existingId}/asset/upload`}
										actionFunction={(options) => dispatch(form_image_upload(options))}
										fileList={medalFiles[m.key] ?? []}
										setFileList={(list) => setMedalFiles({ ...medalFiles, [m.key]: list })}
									/>
								</div>
							</div>
						:	<div style={{ height: 96, border: '1.6px dashed #cdd3d9', borderRadius: 11, background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 5, color: '#8E8E8E' }}>
								<CameraFilled style={{ fontSize: 20 }} />
								<span style={{ fontSize: 12.5, fontWeight: 600 }}>{t('worlds.details.medalImageNote')}</span>
							</div>
					}
				</div>

				<Button
					type='primary'
					block
					style={{ marginTop: 14 }}
					onClick={() => saveMedal(m)}
				>
					{t('worlds.details.saveMedal')}
				</Button>

				<div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(0,0,0,0.06)', fontSize: 11.5, color: style.noteColor, lineHeight: 1.45 }}>
					<InfoCircleOutlined style={{ flex: 'none' }} />
					<span>{isGold ? t('form.label.worldMedalGoldHint') : t('form.label.worldMedalSilverHint')}</span>
				</div>
			</div>
		);
	}

	const fieldLabel = (text, required) => (
		<span style={{ fontSize: 13, fontWeight: 600, color: '#3F3F3F' }}>
			{text}{required && <span style={{ color: '#ff4d4f' }}> *</span>}
		</span>
	);

	return (
		<div style={{ maxWidth: 1000, margin: '0 auto' }}>
			<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
					<Button icon={<LeftOutlined />} style={{ width: 38, height: 38, borderRadius: 10 }} onClick={() => navigate('/game/worlds')} />
					<div>
						<div style={{ fontSize: 12.5, color: '#A0A0A0' }}>
							<a onClick={() => navigate('/game/worlds')}>{t('menuItems.game.worlds')}</a>
							<span> / </span>
							<span style={{ color: '#8E8E8E' }}>{t('worlds.details.title')}</span>
						</div>
						<div style={{ fontSize: 19, fontWeight: 700, color: '#3F3F3F', lineHeight: 1.2 }}>
							{t('worlds.details.title')}
						</div>
					</div>
				</div>
				<div style={{ display: 'flex', gap: 10 }}>
					<Button size='large' onClick={() => navigate('/game/worlds')}>{t('buttons.cancel')}</Button>
					<Button type='primary' size='large' onClick={onSave}>{t('worlds.details.saveWorld')}</Button>
				</div>
			</div>

			{world_info_loading
				?	<Skeleton active paragraph={{ rows: 12 }} />
				:	<Form form={form} layout='vertical' requiredMark={false}>
						{previewCard}

						<div style={cardStyle}>
							{sectionHeader(<UserOutlined style={{ fontSize: 16 }} />, t('worlds.details.identitySection'), t('worlds.details.identityHint'))}
							<Form.Item
								label={fieldLabel(t('form.label.name'), true)}
								name='name'
								rules={[{ required: true, message: t('form.rules.required') }]}
							>
								<Input placeholder='Ej: Mundo Italiano' />
							</Form.Item>
							<Form.Item
								label={fieldLabel(t('form.label.description'), true)}
								name='description'
								rules={[{ required: true, message: t('form.rules.required') }]}
							>
								<Input placeholder='Ej: Cocina de Italia' />
							</Form.Item>
							<div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
								<Form.Item
									label={fieldLabel(t('form.label.nationality'), true)}
									name='nationality'
									rules={[{ required: true, message: t('form.rules.required') }]}
									style={{ flex: 1, minWidth: 220, marginBottom: 0 }}
								>
									<Input placeholder='Ej: Italiana' />
								</Form.Item>
								<Form.Item
									label={fieldLabel(t('worlds.details.colorLabel'), true)}
									name='color'
									rules={[{ required: true, message: t('form.rules.required') }]}
									style={{ marginBottom: 0 }}
								>
									<ColorPicker
										format='hex'
										showText
										presets={[{ label: t('worlds.details.preview'), colors: BRAND_PALETTE }]}
									/>
								</Form.Item>
							</div>
						</div>

						<div style={cardStyle}>
							{sectionHeader(<RiseOutlined style={{ fontSize: 16 }} />, t('worlds.details.progressionSection'), t('worlds.details.progressionHint'))}
							<div style={{ marginBottom: 18 }}>
								<div style={{ marginBottom: 7 }}>
									{fieldLabel(t('worlds.details.orderLabel'))}
								</div>
								<div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
									<Tag style={{ fontSize: 14, padding: '4px 12px' }}>{savedOrder}</Tag>
									<Button size='small' onClick={() => navigate('/game/worlds')}>
										{t('worlds.reorder.modalTitle')}
									</Button>
								</div>
								<div style={{ fontSize: 12, color: '#8E8E8E', marginTop: 6, lineHeight: 1.5 }}>
									{t('worlds.details.orderReadOnly')}
								</div>
							</div>
							{isPremiumWorld
								?	<div style={{ border: '1px solid #EAD5A0', background: 'linear-gradient(180deg, #FFFBF0, #FBF1D6)', borderRadius: 14, padding: '14px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
										<TrophyFilled style={{ color: '#A78334', fontSize: 16, marginTop: 2 }} />
										<span style={{ fontSize: 12.5, color: '#8A6A22', lineHeight: 1.5 }}>
											{t('worlds.details.premiumWorldNote')}
										</span>
									</div>
								:	starGoalPanel}
						</div>

						<WorldPacksCard messageApi={messageApi} worldId={world_id} />

						<div style={cardStyle}>
							{sectionHeader(<PictureFilled style={{ fontSize: 16 }} />, t('worlds.details.assetsSection'), t('worlds.details.assetsHint'))}
							<div style={{ display: 'flex', gap: 30, flexWrap: 'wrap' }}>
								<div>
									<label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#3F3F3F', marginBottom: 7 }}>
										{t('form.label.pattern')} <span style={{ fontSize: 12, fontWeight: 400, color: '#A0A0A0' }}>{t('worlds.details.optional')}</span>
									</label>
									<ImageUpload
										action={process.env.REACT_APP_SERVER_URL + `/api/game/worlds/${world_id}/pattern/upload`}
										actionFunction={(options) => dispatch(form_image_upload(options))}
										fileList={patternFile}
										setFileList={setPatternFile}
									/>
								</div>
								<div>
									<label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#3F3F3F', marginBottom: 7 }}>
										{t('form.label.image')} <span style={{ fontSize: 12, fontWeight: 400, color: '#A0A0A0' }}>{t('worlds.details.optional')}</span>
									</label>
									<ImageUpload
										action={process.env.REACT_APP_SERVER_URL + `/api/game/worlds/${world_id}/image/upload`}
										actionFunction={(options) => dispatch(form_image_upload(options))}
										fileList={imageFile}
										setFileList={setImageFile}
									/>
								</div>
							</div>
						</div>

						<div style={cardStyle}>
							{sectionHeader(<TrophyFilled style={{ fontSize: 16 }} />, t('form.label.worldMedalsSection'), t('worlds.details.medalsHint'))}
							<div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 18, alignItems: 'start' }}>
								{medals.map((m) => medalCard(m))}
								{medals.length < 2 && (
									<button
										type='button'
										onClick={addMedal}
										style={{ minHeight: 230, border: '1.6px dashed #d9d4cd', borderRadius: 14, background: '#FBFAF9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, cursor: 'pointer', color: '#8E8E8E', padding: 24 }}
									>
										<span style={{ width: 48, height: 48, borderRadius: '50%', background: '#FFEDE9', color: '#B75D46', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
											<PlusOutlined style={{ fontSize: 22 }} />
										</span>
										<span style={{ fontSize: 14, fontWeight: 700 }}>{t('worlds.details.addMedal')}</span>
										<span style={{ fontSize: 12, color: '#A0A0A0', lineHeight: 1.5, textAlign: 'center', maxWidth: 210 }}>
											{t('worlds.details.addMedalHint')}
										</span>
									</button>
								)}
							</div>
						</div>
					</Form>
			}
		</div>
	)
}

WorldDetails.propTypes = {
	messageApi: PropTypes.object.isRequired
}

export default WorldDetails;
